import { api } from './api';
import { useAuthStore } from '../stores/authStore';

/**
 * Admin API wrappers.
 *
 * Each call attaches the admin JWT from `useAuthStore` as a Bearer token.
 * The shared `api` wrapper unwraps the REST-SPEC envelope's `data` field.
 */

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface ReviewQueueItem {
  id: string;
  rawListingId: string | null;
  normalizedProductId: string | null;
  reason: string;
  confidenceScore: string | number | null;
  status: 'pending' | 'in_review' | 'resolved' | 'dismissed';
  reviewedBy: string | null;
  resolvedAt: string | null;
  notes: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ReviewQueueListResponse {
  items: ReviewQueueItem[];
  pagination: { total: number; page: number; limit: number };
}

export interface RawListingSummary {
  id: string;
  retailerId: string | null;
  sourceUrl: string;
  rawTitle: string | null;
  rawData?: unknown;
}

export interface NormalizedProductFields {
  brand?: string | null;
  productLine?: string | null;
  caliber?: string | null;
  bulletWeightGrains?: number | null;
  bulletType?: string | null;
  caseMaterial?: string | null;
  primerType?: string | null;
  useCases?: string[] | null;
  isReloaded?: boolean | null;
  isSubsonic?: boolean | null;
  isLeadFree?: boolean | null;
  upc?: string | null;
  manufacturerPartNumber?: string | null;
}

export interface NormalizedProduct extends NormalizedProductFields {
  id: string;
}

export interface RetailerSummary {
  id: string;
  name: string;
  slug: string;
}

export interface ReviewQueueDetail {
  item: ReviewQueueItem;
  rawListing: RawListingSummary | null;
  normalizedProduct: NormalizedProduct | null;
  retailer: RetailerSummary | null;
}

export interface AdminStats {
  totalUsers: number;
  totalRetailers: number;
  activeRetailers: number;
  activeListings: number;
  pendingReviewItems: number;
  activeAlerts: number;
}

// ── Usage analytics (#212) ──────────────────────────────────────────────────

export interface AdminAnalytics {
  windowHours: number;
  topSearches: Array<{ query: string; count: number }>;
  popularCalibers: Array<{ caliber: string; count: number }>;
  topClickedRetailers: Array<{ retailerId: string; retailerName: string | null; count: number }>;
}

export interface AnalyticsParams {
  windowHours?: number;
  limit?: number;
  [key: string]: string | number | undefined;
}

export type UserRole = 'user' | 'admin' | 'advertiser';

/** Non-sensitive user fields returned by the admin user-management endpoints. */
export interface AdminUser {
  id: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  /** Non-null ISO timestamp when the user is banned (for-cause hard block). */
  bannedAt?: string | null;
  banReason?: string | null;
  /** Non-null ISO timestamp when the user verified their email (#209); null/
   * undefined = unverified. */
  emailVerifiedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminUserListResponse {
  users: AdminUser[];
  pagination: { total: number; page: number; limit: number };
}

/** A user's safe profile + recent activity streams (#211). */
export interface AdminUserDetail {
  user: AdminUser;
  recentSearches: Array<{
    id: string;
    query: string | null;
    caliber: string | null;
    createdAt?: string;
  }>;
  recentClicks: Array<{
    id: string;
    retailerId: string | null;
    retailerName: string | null;
    productListingId: string | null;
    sourceUrl: string;
    createdAt?: string;
  }>;
  savedSearches: Array<{ id: string; name: string; query: string | null; isAlertBacked?: boolean }>;
  watchlist: Array<{ id: string; productListingId: string; notes?: string | null }>;
  alerts: Array<{ id: string; name: string; isActive: boolean; lastFiredAt?: string | null }>;
}

export interface ListUsersParams {
  search?: string;
  role?: UserRole;
  isActive?: boolean;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | undefined;
}

export interface ListParams {
  status?: 'pending' | 'in_review' | 'resolved' | 'dismissed';
  reason?: string;
  page?: number;
  limit?: number;
  [key: string]: string | number | undefined;
}

export type AdvertiserStatus = 'pending' | 'active' | 'suspended';

/** Non-sensitive advertiser-account fields returned by the admin endpoints. */
export interface AdminAdvertiser {
  id: string;
  userId: string;
  companyName: string;
  contactEmail: string;
  status: AdvertiserStatus;
  campaignCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminAdvertiserListResponse {
  advertisers: AdminAdvertiser[];
  pagination: { total: number; page: number; limit: number };
}

export interface AdvertiserCampaign {
  id: string;
  name: string;
  status: string;
  dailyBudgetCents: number | null;
  totalBudgetCents: number | null;
  spentCents: number;
  startsAt: string | null;
  endsAt: string | null;
  createdAt?: string;
}

export interface AdvertiserInvoice {
  id: string;
  periodStart: string;
  periodEnd: string;
  totalCents: number;
  status: string;
  issuedAt: string | null;
  paidAt: string | null;
}

export interface AdvertiserBillingSummary {
  invoiceCount: number;
  totalBilledCents: number;
  outstandingCents: number;
  byStatus: Record<string, number>;
  invoices: AdvertiserInvoice[];
}

export interface AdminAdvertiserDetail {
  advertiser: AdminAdvertiser;
  campaigns: AdvertiserCampaign[];
  billing: AdvertiserBillingSummary;
}

export interface ListAdvertisersParams {
  search?: string;
  status?: AdvertiserStatus;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | undefined;
}

// ── Statistics extensions (GitHub #12 area 5) ───────────────────────────────

/** Per-retailer scrape-health row (aggregate operational metadata, no PII). */
export interface ScrapeHealthRetailer {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  isDegraded: boolean;
  isStale: boolean;
  lastScrapedAt: string | null;
  scrapeIntervalMinutes: number;
  freshnessWindowMinutes: number;
  rawListingsTotal: number;
  rawListingsRecent: number;
  rawListingsUnprocessed: number;
}

export interface ScrapeHealthResponse {
  generatedAt: string;
  windowHours: number;
  summary: {
    totalRetailers: number;
    degradedRetailers: number;
    staleRetailers: number;
  };
  retailers: ScrapeHealthRetailer[];
}

export interface ScrapeHealthParams {
  windowHours?: number;
  activeOnly?: boolean;
  [key: string]: string | number | boolean | undefined;
}

/** A persisted scraper crawl failure (issue #213; operational metadata, no PII). */
export interface ScrapeErrorRow {
  id: string;
  retailerId: string | null;
  retailerSlug: string | null;
  retailerName: string | null;
  url: string | null;
  errorType: string | null;
  message: string | null;
  httpStatus: number | null;
  createdAt: string | null;
}

export interface ScrapeErrorListResponse {
  errors: ScrapeErrorRow[];
  pagination: { total: number; page: number; limit: number };
}

export interface ListScrapeErrorsParams {
  retailerId?: string;
  errorType?: string;
  page?: number;
  limit?: number;
  [key: string]: string | number | undefined;
}

export type AuditActorType = 'user' | 'admin' | 'advertiser' | 'system';

export interface AuditLogEntry {
  id: string;
  actorId: string | null;
  actorType: AuditActorType;
  action: string;
  entityType: string | null;
  entityId: string | null;
  payload: unknown;
  ipAddress: string | null;
  createdAt?: string;
}

export interface AuditLogListResponse {
  entries: AuditLogEntry[];
  pagination: { total: number; page: number; limit: number };
}

export interface ListAuditLogParams {
  action?: string;
  actorType?: AuditActorType;
  actorId?: string;
  entityType?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | undefined;
}

// ── Retailer management (GitHub #12 area 1) ─────────────────────────────────

/** Operational retailer fields returned by the admin retailer endpoints. */
export interface AdminRetailer {
  id: string;
  name: string;
  slug: string;
  baseUrl: string;
  logoUrl: string | null;
  trustScore: number | null;
  scrapeIntervalMinutes: number;
  freshnessWindowMinutes: number;
  isActive: boolean;
  isDegraded: boolean;
  /** When true, this retailer's matching listings are pinned to the top of
   * search, labeled "Promoted" (#149, §23). Editorial boost, not behavioral. */
  isPromoted: boolean;
  /** Optional structured-feed ingestion (#274). When set, this retailer is
   * ingested from its feed instead of being scraped. */
  feedUrl: string | null;
  feedFormat: 'ammoseek' | 'roundrecon' | null;
  lastScrapedAt: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminRetailerListResponse {
  retailers: AdminRetailer[];
  pagination: { total: number; page: number; limit: number };
}

export interface AdminRetailerDetail {
  retailer: AdminRetailer;
  listings: { total: number; inStock: number };
}

export interface ListRetailersParams {
  search?: string;
  isActive?: boolean;
  isDegraded?: boolean;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | undefined;
}

/** Admin-controllable retailer fields (trust score, active, degraded, promoted). */
export interface PatchRetailerBody {
  trustScore?: number;
  isActive?: boolean;
  isDegraded?: boolean;
  isPromoted?: boolean;
  /** Feed ingestion (#274). Send both together; an empty url + null format clears it. */
  feedUrl?: string | null;
  feedFormat?: 'ammoseek' | 'roundrecon' | null;
}

// ── Scrape-job / results viewing (GitHub #12 area 2) ────────────────────────

/** A reconstructed per-retailer scrape run (read-only history). */
export interface ScrapeJob {
  retailerId: string;
  retailerName: string | null;
  retailerSlug: string | null;
  runAt: string;
  status: 'completed' | 'processing';
  listingsScraped: number;
  listingsProcessed: number;
  listingsUnprocessed: number;
}

export interface ScrapeJobsResponse {
  jobs: ScrapeJob[];
  pagination: { total: number; page: number; limit: number };
  windowHours: number;
}

export interface ListScrapeJobsParams {
  retailerId?: string;
  windowHours?: number;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | undefined;
}

// ── Alert-performance metrics (GitHub #12 area 3) ───────────────────────────

export interface AlertPerformanceResponse {
  generatedAt: string;
  windowHours: number;
  alerts: {
    evaluated: number;
    fired: number;
    deduped: number;
    quietHours: number;
    frequencyCap: number;
    noMatch: number;
    byOutcome: Record<string, number>;
  };
  notifications: {
    sent: number;
    total: number;
    byStatus: Record<string, number>;
  };
}

export interface AlertPerformanceParams {
  windowHours?: number;
  [key: string]: string | number | boolean | undefined;
}

// ── Affiliate program management (ADR 0006, GitHub #12 affiliate vertical) ──

export type AffiliateNetwork =
  | 'direct'
  | 'impact'
  | 'avantlink'
  | 'cj'
  | 'awin'
  | 'partnerize'
  | 'other';
export type CommissionType = 'percentage' | 'flat_per_order' | 'flat_per_item' | 'tiered';
export type ProgramStatus = 'pending' | 'active' | 'paused' | 'terminated';
export type AffiliateLinkType = 'deep_link_template' | 'param_append' | 'static';

export interface AffiliateProgram {
  id: string;
  retailerId: string;
  network: AffiliateNetwork;
  networkProgramId: string | null;
  commissionType: CommissionType;
  commissionRateBps: number | null;
  commissionFlatCents: number | null;
  commissionTiers: { minCents: number; rateBps: number }[] | null;
  cookieWindowDays: number;
  currency: string;
  status: ProgramStatus;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  notes: string | null;
  clickCount?: number;
  conversionCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface AffiliateLink {
  id: string;
  affiliateProgramId: string;
  retailerId: string;
  linkType: AffiliateLinkType;
  template: string;
  paramRules: Record<string, string | number | boolean> | null;
  subIdParam: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AffiliateProgramListResponse {
  programs: AffiliateProgram[];
  pagination: { total: number; page: number; limit: number };
}

export interface AffiliateProgramDetail {
  program: AffiliateProgram;
  links: AffiliateLink[];
  summary: {
    clickCount: number;
    conversionCount: number;
    earnedCents: number;
    pendingCents: number;
    paidCents: number;
    byStatus: Record<string, number>;
  };
}

export interface ListAffiliateProgramsParams {
  network?: AffiliateNetwork;
  status?: ProgramStatus;
  page?: number;
  limit?: number;
  [key: string]: string | number | undefined;
}

export interface CreateAffiliateProgramBody {
  retailerId: string;
  network: AffiliateNetwork;
  networkProgramId?: string | null;
  commissionType: CommissionType;
  commissionRateBps?: number | null;
  commissionFlatCents?: number | null;
  cookieWindowDays?: number;
  currency?: string;
  status?: ProgramStatus;
  notes?: string | null;
}

export type PatchAffiliateProgramBody = Partial<Omit<CreateAffiliateProgramBody, 'retailerId'>>;

export interface CreateAffiliateLinkBody {
  linkType: AffiliateLinkType;
  template: string;
  paramRules?: Record<string, string | number | boolean> | null;
  subIdParam?: string | null;
  isActive?: boolean;
}

export type PatchAffiliateLinkBody = Partial<CreateAffiliateLinkBody>;

export interface AffiliateReportRetailerRow {
  retailerId: string;
  clicks: number;
  conversions: number;
  conversionRate: number;
  earnedCents: number;
  pendingCents: number;
  paidCents: number;
}

export interface AffiliateReportResponse {
  generatedAt: string;
  windowHours: number;
  filters: { network: AffiliateNetwork | null; retailerId: string | null };
  totals: {
    clicks: number;
    conversions: number;
    conversionRate: number;
    earnedCents: number;
    pendingCents: number;
    paidCents: number;
  };
  retailers: AffiliateReportRetailerRow[];
}

export interface AffiliateReportsParams {
  network?: AffiliateNetwork;
  retailerId?: string;
  windowHours?: number;
  [key: string]: string | number | undefined;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

export const adminApi = {
  getStats(): Promise<AdminStats> {
    return api.get<AdminStats>('/admin/stats', { headers: authHeaders() });
  },

  getAnalytics(params: AnalyticsParams = {}): Promise<AdminAnalytics> {
    return api.get<AdminAnalytics>(`/admin/analytics${qs(params)}`, { headers: authHeaders() });
  },

  getUser(id: string): Promise<AdminUserDetail> {
    return api.get<AdminUserDetail>(`/admin/users/${id}`, { headers: authHeaders() });
  },

  listReviewQueue(params: ListParams = {}): Promise<ReviewQueueListResponse> {
    return api.get<ReviewQueueListResponse>(`/admin/review-queue${qs(params)}`, {
      headers: authHeaders(),
    });
  },

  getReviewQueueItem(id: string): Promise<ReviewQueueDetail> {
    return api.get<ReviewQueueDetail>(`/admin/review-queue/${id}`, { headers: authHeaders() });
  },

  patchReviewQueueItem(
    id: string,
    patch: {
      status?: 'pending' | 'in_review' | 'resolved' | 'dismissed';
      normalizedProductFields?: NormalizedProductFields;
      notes?: string;
      addAliases?: string[];
    }
  ): Promise<{ item: ReviewQueueItem }> {
    return api.patch(`/admin/review-queue/${id}`, patch, { headers: authHeaders() });
  },

  listUsers(params: ListUsersParams = {}): Promise<AdminUserListResponse> {
    return api.get<AdminUserListResponse>(`/admin/users${qs(params)}`, {
      headers: authHeaders(),
    });
  },

  patchUser(
    id: string,
    patch: { role?: UserRole; isActive?: boolean }
  ): Promise<{ user: AdminUser }> {
    return api.patch(`/admin/users/${id}`, patch, { headers: authHeaders() });
  },

  /** Ban a user for cause (#210). Reason is required. */
  banUser(id: string, reason: string): Promise<{ user: AdminUser }> {
    return api.post(`/admin/users/${id}/ban`, { reason }, { headers: authHeaders() });
  },

  /** Lift a ban (#210). */
  unbanUser(id: string): Promise<{ user: AdminUser }> {
    return api.post(`/admin/users/${id}/unban`, {}, { headers: authHeaders() });
  },

  /** Manually mark a user's email verified (#209). */
  verifyUser(id: string): Promise<{ user: AdminUser }> {
    return api.post(`/admin/users/${id}/verify`, {}, { headers: authHeaders() });
  },

  /** Re-send a user's verification email on their behalf (#209). */
  resendVerification(id: string): Promise<{ user: AdminUser }> {
    return api.post(`/admin/users/${id}/resend-verification`, {}, { headers: authHeaders() });
  },

  listAdvertisers(params: ListAdvertisersParams = {}): Promise<AdminAdvertiserListResponse> {
    return api.get<AdminAdvertiserListResponse>(`/admin/advertisers${qs(params)}`, {
      headers: authHeaders(),
    });
  },

  getAdvertiser(id: string): Promise<AdminAdvertiserDetail> {
    return api.get<AdminAdvertiserDetail>(`/admin/advertisers/${id}`, {
      headers: authHeaders(),
    });
  },

  patchAdvertiser(
    id: string,
    patch: { status: AdvertiserStatus }
  ): Promise<{ advertiser: AdminAdvertiser }> {
    return api.patch(`/admin/advertisers/${id}`, patch, { headers: authHeaders() });
  },

  getScrapeHealth(params: ScrapeHealthParams = {}): Promise<ScrapeHealthResponse> {
    return api.get<ScrapeHealthResponse>(`/admin/scrape-results${qs(params)}`, {
      headers: authHeaders(),
    });
  },

  listScrapeErrors(params: ListScrapeErrorsParams = {}): Promise<ScrapeErrorListResponse> {
    return api.get<ScrapeErrorListResponse>(`/admin/scrape-errors${qs(params)}`, {
      headers: authHeaders(),
    });
  },

  listAuditLog(params: ListAuditLogParams = {}): Promise<AuditLogListResponse> {
    return api.get<AuditLogListResponse>(`/admin/audit-log${qs(params)}`, {
      headers: authHeaders(),
    });
  },

  listRetailers(params: ListRetailersParams = {}): Promise<AdminRetailerListResponse> {
    return api.get<AdminRetailerListResponse>(`/admin/retailers${qs(params)}`, {
      headers: authHeaders(),
    });
  },

  getRetailer(id: string): Promise<AdminRetailerDetail> {
    return api.get<AdminRetailerDetail>(`/admin/retailers/${id}`, { headers: authHeaders() });
  },

  patchRetailer(id: string, patch: PatchRetailerBody): Promise<{ retailer: AdminRetailer }> {
    return api.patch(`/admin/retailers/${id}`, patch, { headers: authHeaders() });
  },

  listScrapeJobs(params: ListScrapeJobsParams = {}): Promise<ScrapeJobsResponse> {
    return api.get<ScrapeJobsResponse>(`/admin/scrape-jobs${qs(params)}`, {
      headers: authHeaders(),
    });
  },

  getAlertPerformance(params: AlertPerformanceParams = {}): Promise<AlertPerformanceResponse> {
    return api.get<AlertPerformanceResponse>(`/admin/alert-performance${qs(params)}`, {
      headers: authHeaders(),
    });
  },

  // ── Affiliate program management (ADR 0006) ──────────────────────────────
  listAffiliatePrograms(
    params: ListAffiliateProgramsParams = {}
  ): Promise<AffiliateProgramListResponse> {
    return api.get<AffiliateProgramListResponse>(`/admin/affiliates${qs(params)}`, {
      headers: authHeaders(),
    });
  },

  getAffiliateProgram(id: string): Promise<AffiliateProgramDetail> {
    return api.get<AffiliateProgramDetail>(`/admin/affiliates/${id}`, { headers: authHeaders() });
  },

  createAffiliateProgram(body: CreateAffiliateProgramBody): Promise<{ program: AffiliateProgram }> {
    return api.post('/admin/affiliates', body, { headers: authHeaders() });
  },

  patchAffiliateProgram(
    id: string,
    patch: PatchAffiliateProgramBody
  ): Promise<{ program: AffiliateProgram }> {
    return api.patch(`/admin/affiliates/${id}`, patch, { headers: authHeaders() });
  },

  createAffiliateLink(
    programId: string,
    body: CreateAffiliateLinkBody
  ): Promise<{ link: AffiliateLink }> {
    return api.post(`/admin/affiliates/${programId}/links`, body, { headers: authHeaders() });
  },

  patchAffiliateLink(
    linkId: string,
    patch: PatchAffiliateLinkBody
  ): Promise<{ link: AffiliateLink }> {
    return api.patch(`/admin/affiliate-links/${linkId}`, patch, { headers: authHeaders() });
  },

  getAffiliateReports(params: AffiliateReportsParams = {}): Promise<AffiliateReportResponse> {
    return api.get<AffiliateReportResponse>(`/admin/affiliates/reports${qs(params)}`, {
      headers: authHeaders(),
    });
  },
};

export default adminApi;
